import { Problem, Difficulty, Example } from '../types/Problem';

export interface LeetCodePreferences {
  title: string;
  difficulty: string;
  description: string;
  examples: string;
  constraints: string;
}

// Parse examples into the expected format
export const parseExamples = (examplesText: string): Example[] => {
  if (!examplesText.trim()) return [];

  // Split by "Example" and filter out empty entries
  const exampleBlocks = examplesText
    .split(/Example\s*\d*:/i)
    .filter((block) => block.trim());

  return exampleBlocks.map((block, index) => {
    const lines = block
      .trim()
      .split('\n')
      .map((line) => line.trim());
    let input = '';
    let output = '';
    let explanation = '';

    for (const line of lines) {
      if (line.toLowerCase().startsWith('input:')) {
        input = line.substring(6).trim();
      } else if (line.toLowerCase().startsWith('output:')) {
        output = line.substring(7).trim();
      } else if (line.toLowerCase().startsWith('explanation:')) {
        explanation = line.substring(12).trim();
      }
    }

    return {
      input: input || `Example ${index + 1} input`,
      output: output || `Example ${index + 1} output`,
      explanation: explanation || undefined,
    };
  });
};

// Parse constraints into array
export const parseConstraints = (constraintsText: string) => {
  if (!constraintsText.trim()) return [];
  return constraintsText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line)
    .filter((line) => !line.toLowerCase().match(/^constraints?\s*:?\s*$/i)) // Remove "Constraints:" header
    .map((line) => {
      // Convert common LeetCode number formats to proper mathematical notation
      // 104 -> 10^4, 105 -> 10^5, 109 -> 10^9, etc.
      return line.replace(/\b10(\d)\b/g, '10^$1');
    });
};

// Function to create Problem object from LeetCode preferences
export const createLeetCodeProblem = (
  leetCodePrefs: LeetCodePreferences
): Problem => {
  return {
    id: `leetcode-${Date.now()}`, // Generate a unique ID
    title: leetCodePrefs.title,
    difficulty: leetCodePrefs.difficulty as Difficulty,
    description: leetCodePrefs.description,
    examples: parseExamples(leetCodePrefs.examples),
    constraints: parseConstraints(leetCodePrefs.constraints),
    tags: [], // No tags for custom LeetCode problems
    company: 'Meta',
  };
};
